import React from 'react';
import { ArrowLeftIcon, CreditCardIcon } from '@heroicons/react/24/outline';

const PaymentModal = ({ 
  registrationData, 
  paymentData, 
  setPaymentData, 
  onSubmit, 
  paymentStatus, 
  error, 
  onBack 
}) => {
  const { selectedProvince, selectedClub } = registrationData;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800 rounded-xl p-8 shadow-2xl">
        {/* Header with back button */}
        <div className="flex items-center mb-8">
          <button
            onClick={onBack}
            disabled={paymentStatus === 'processing'}
            className="flex items-center text-gray-400 hover:text-white mr-4 transition-colors disabled:opacity-50"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">Complete Payment</h1>
            <p className="text-gray-400">Enter your payment details to complete registration</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Payment Form */}
          <div className="space-y-6">
            <div className="bg-gray-700 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <CreditCardIcon className="h-6 w-6 text-red-500 mr-2" />
                <h2 className="text-xl font-bold text-white">Payment Information</h2>
              </div>

              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={paymentData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-600 text-white rounded-lg p-3 border border-gray-500 focus:border-red-500 focus:outline-none"
                    placeholder="Enter your email"
                    disabled={paymentStatus === 'processing'}
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={paymentData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-600 text-white rounded-lg p-3 border border-gray-500 focus:border-red-500 focus:outline-none"
                    placeholder="Enter your phone number"
                    disabled={paymentStatus === 'processing'}
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Payment Method</label>
                  <select
                    name="operator"
                    value={paymentData.operator}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-600 text-white rounded-lg p-3 border border-gray-500 focus:border-red-500 focus:outline-none"
                    disabled={paymentStatus === 'processing'}
                  >
                    <option value="mtn">MTN Mobile Money</option>
                    <option value="airtel">Airtel Money</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={paymentStatus === 'processing'}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {paymentStatus === 'processing' ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing Payment...
                    </div>
                  ) : (
                    `Pay K${paymentData.amountPaid}`
                  )}
                </button>

                {error && (
                  <div className="bg-red-900/50 border border-red-500 rounded-lg p-4">
                    <p className="text-red-400">{error}</p>
                  </div>
                )}

                {paymentStatus === 'success' && (
                  <div className="bg-green-900/50 border border-green-500 rounded-lg p-4">
                    <p className="text-green-400">Payment successful! Redirecting...</p>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="space-y-6">
            <div className="bg-gray-700 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Order Summary</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Club:</span>
                  <span className="text-white font-semibold">{selectedClub?.name}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">League:</span>
                  <span className="text-white font-semibold">{selectedProvince?.name}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Season:</span>
                  <span className="text-white font-semibold">{registrationData.seasonYear}</span>
                </div>
                
                <div className="border-t border-gray-600 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Registration Fee:</span>
                    <span className="text-white font-semibold">K{selectedProvince?.registrationFee || 210.00}</span>
                  </div>
                </div>
                
                <div className="border-t border-gray-600 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-white">Total:</span>
                    <span className="text-xl font-bold text-red-500">K{paymentData.amountPaid}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Instructions */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-400 mb-3">Payment Instructions</h3>
              <ul className="text-gray-300 space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="text-blue-400 mr-2">•</span>
                  You will receive a payment prompt on your phone
                </li>
                <li className="flex items-start">
                  <span className="text-blue-400 mr-2">•</span>
                  Enter your mobile money PIN when prompted
                </li>
                <li className="flex items-start">
                  <span className="text-blue-400 mr-2">•</span>
                  Wait for payment confirmation before closing this page
                </li>
                <li className="flex items-start">
                  <span className="text-blue-400 mr-2">•</span>
                  You will be redirected automatically after successful payment
                </li>
              </ul>
            </div>

            {/* Security Notice */}
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center text-green-400 text-sm">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Secure Payment Gateway
              </div>
              <p className="text-gray-400 text-xs mt-1">
                Your payment information is encrypted and secure
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal; 